import React from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import SectionHeader from '../components/SectionHeader';
import { Shield, Lock, Eye, FileText, Server, Cookie, Share2, Trash2, UserCheck, Mail } from 'lucide-react';

const PrivacySection = ({ title, icon: Icon, children, delay }) => (
  <div 
    className="bg-card border border-white/5 p-8 rounded-xl mb-8 hover:border-primary/30 transition-all duration-300"
    data-aos="fade-up"
    data-aos-delay={delay}
  >
    <div className="flex items-center gap-4 mb-6 border-b border-white/5 pb-4">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
        <Icon size={20} />
      </div>
      <h3 className="text-xl font-serif font-bold text-foreground">{title}</h3>
    </div>
    <div className="text-muted-foreground leading-relaxed space-y-4">
      {children}
    </div>
  </div>
);

const Privacy = () => {
  return (
    <Layout>
      <PageHeader 
        title="Privacy Policy" 
        subtitle="Your Data. Our Responsibility."
        image="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop"
      />

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16" data-aos="fade-up">
            <p className="text-lg text-muted-foreground">
              At TOTO INFRA, we value your trust. This Privacy Policy outlines how we collect, use, and protect your personal information when you visit our website or engage with our services.
            </p>
            <p className="text-primary font-medium mt-4">
              By accessing this website, you agree to the terms of this Privacy Policy.
            </p>
          </div>

          <PrivacySection title="Information We Collect" icon={FileText} delay="0">
            <p>We may collect the following types of information:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Personal Information:</strong> Name, email address, phone number, and company details provided voluntarily via forms.</li>
              <li><strong>Project Details:</strong> Specific requirements or queries related to your infrastructure needs.</li>
              <li><strong>Automated Data:</strong> IP address, browser type, device information, and usage patterns collected via cookies and analytics tools.</li>
            </ul>
          </PrivacySection>

          <PrivacySection title="How We Use Your Information" icon={Server} delay="100">
            <ul className="list-disc pl-5 space-y-2">
              <li>To respond to your enquiries and provide project consultations.</li>
              <li>To communicate updates regarding our services and projects.</li>
              <li>To improve our website functionality and user experience.</li>
              <li>For internal analytics and strategic planning.</li>
              <li>To comply with legal obligations and regulatory requirements.</li>
            </ul>
          </PrivacySection>

          <PrivacySection title="Data Security" icon={Lock} delay="200">
            <p>
              We implement industry-standard security measures to protect your data from unauthorized access, alteration, disclosure, or destruction. Access to your personal information is restricted to authorized personnel only.
            </p>
          </PrivacySection>

          <PrivacySection title="Cookies Policy" icon={Cookie} delay="300">
            <p>
              Our website uses cookies to enhance your browsing experience and analyze site traffic. You can choose to disable cookies through your browser settings, though this may affect some website functionalities.
            </p>
          </PrivacySection>

          <PrivacySection title="Third-Party Sharing" icon={Share2} delay="400">
            <p>
              We do not sell, trade, or rent your personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our business partners and trusted affiliates.
            </p>
          </PrivacySection>

          <PrivacySection title="Data Retention" icon={Trash2} delay="500">
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements.
            </p>
          </PrivacySection>

          <PrivacySection title="Your Rights" icon={UserCheck} delay="600">
            <ul className="list-disc pl-5 space-y-2">
              <li>Right to access the personal data we hold about you.</li>
              <li>Right to request correction of inaccurate data.</li>
              <li>Right to request deletion of your data.</li>
              <li>Right to withdraw consent for data processing.</li>
            </ul>
          </PrivacySection>

          <div className="mt-16 pt-16 border-t border-white/10 text-center" data-aos="fade-up">
            <SectionHeader title="Contact Us" subtitle="Privacy Concerns" />
            <div className="flex flex-col md:flex-row justify-center gap-8 mt-8">
              <div className="bg-card p-6 rounded-xl border border-white/5">
                <Mail className="w-8 h-8 text-primary mx-auto mb-4" />
                <p className="text-foreground font-bold">Privacy Officer</p>
                <a href="mailto:sandhya@totoinfra.com" className="text-muted-foreground hover:text-primary transition-colors">sandhya@totoinfra.com</a>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-8">Last Updated: November 2024</p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Privacy;
